import React, { useCallback, useEffect, useState, useMemo } from "react";
import { toast } from "react-toastify";
import {
  Mail,
  CheckCircle2,
  Circle,
  Eye,
  Building2,
  ShieldCheck,
  Search,
  Monitor,
  Smartphone,
  Copy,
  Check,
  X,
} from "lucide-react";

import Header from "../../Components/Header";
import Footer from "../../Components/Footer";
import loader from "../../../methods/loader";
import FormControl from "../../../components/common/FormControl";
import {
  MODULE_CONFIG,
  STANDARD_EMAIL_TYPES,
  EMAIL_TYPE_MAP,
  TenantEmailTemplateService,
} from "./shared";

export default function TenantEmailTemplatesList() {
  // Active email type (default: contact_reply per user specification)
  const [activeType, setActiveType] = useState("contact_reply");

  // Search & data states (strictly dynamic from API)
  const [searchQuery, setSearchQuery] = useState("");
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);

  // Live preview modal states
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [previewDevice, setPreviewDevice] = useState("desktop");
  const [copiedCode, setCopiedCode] = useState(false);

  // Fetch available templates strictly dynamically from the API
  const fetchTemplates = useCallback(
    async (emailType, showLoader = false) => {
      if (showLoader) loader(true);
      setLoading(true);

      try {
        const res = await TenantEmailTemplateService.getAvailable(emailType);

        if (res?.success && (res?.data || res?.templates)) {
          const data = res?.data || res;
          let allList = [];
          let activeId = data?.selectedTemplateId || data?.active_template_id || null;

          // If backend returns separated arrays
          if (Array.isArray(data?.organizationTemplates) || Array.isArray(data?.platformTemplates)) {
            const orgs = (data.organizationTemplates || []).map((t) => ({
              ...t,
              source: "organization",
            }));
            const plts = (data.platformTemplates || []).map((t) => ({
              ...t,
              source: "platform",
            }));
            allList = [...orgs, ...plts];
          } else if (Array.isArray(data?.templates) || Array.isArray(data?.data) || Array.isArray(data)) {
            // If backend returns flat list
            const raw = data.templates || data.data || (Array.isArray(data) ? data : []);
            allList = raw.map((item) => {
              const isOrg =
                item?.source === "organization" ||
                item?.source === "tenant" ||
                Boolean(item?.tenant_id) ||
                Boolean(item?.is_organization);
              return {
                ...item,
                source: isOrg ? "organization" : "platform",
              };
            });
          }

          // Dynamic active ID
          if (!activeId) {
            const marked = allList.find((t) => t.is_selected || t.is_active);
            activeId = marked?.id || marked?._id || (allList.length > 0 ? allList[0]?.id || allList[0]?._id : null);
          }

          setTemplates(allList);
          setSelectedTemplateId(activeId);
        } else {
          setTemplates([]);
          setSelectedTemplateId(null);
        }
      } catch (err) {
        console.error("Failed to load templates from API:", err);
        setTemplates([]);
        setSelectedTemplateId(null);
      } finally {
        setLoading(false);
        if (showLoader) loader(false);
      }
    },
    []
  );

  useEffect(() => {
    fetchTemplates(activeType, true);
  }, [activeType, fetchTemplates]);

  // Select which email template should be followed for outgoing transactional emails
  const handleSelectTemplate = async (template) => {
    const templateId = template?.id || template?._id;
    if (!templateId || templateId === selectedTemplateId) return;

    setIsUpdating(true);
    loader(true);

    try {
      // POST http://localhost:6002/email-templates/tenant/select { email_type, templateId }
      const res = await TenantEmailTemplateService.selectTemplate({
        templateId,
        emailType: activeType,
      });

      if (res?.success) {
        setSelectedTemplateId(templateId);
        toast.success(`Active template updated to "${template.name}". Outgoing emails will now follow this template.`);
      } else {
        toast.error(res?.message || "Failed to update active template.");
      }
    } catch (err) {
      console.error("Select template error:", err);
      toast.error("Could not update template selection.");
    } finally {
      setIsUpdating(false);
      loader(false);
    }
  };

  // Filter templates dynamically by search query
  const filteredTemplates = useMemo(() => {
    if (!searchQuery.trim()) return templates;
    const q = searchQuery.toLowerCase().trim();
    return templates.filter(
      (t) =>
        t.name?.toLowerCase().includes(q) ||
        t.subject?.toLowerCase().includes(q) ||
        t.description?.toLowerCase().includes(q)
    );
  }, [templates, searchQuery]);

  const currentTypeConfig = EMAIL_TYPE_MAP[activeType] || {
    name: "Contact Reply",
    description: "Automated response sent to customers when they submit an inquiry or contact message.",
  };

  const currentActiveTemplate = templates.find(
    (t) => (t.id || t._id) === selectedTemplateId
  );

  const handleCopyHtml = (html) => {
    if (!html) return;
    navigator.clipboard.writeText(html);
    setCopiedCode(true);
    toast.info("Email HTML template copied!");
    setTimeout(() => setCopiedCode(false), 2000);
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#F4F7FB] antialiased text-slate-800">
      <Header />

      {/* Main Container with correct spacing below fixed header and above floating dock */}
      <div className="min-h-[calc(100vh_-_60px)] md:min-h-[calc(100vh_-_70px)] mt-[60px] lg:mt-[70px] py-6 lg:py-8">
        <div className="container mx-auto px-3 sm:px-6 lg:px-8 max-w-5xl space-y-6 pb-36">

          {/* ─── 1. PAGE TITLE & DESCRIPTION ─── */}
          <div>
            <h1 className="text-2xl sm:text-3xl font-black text-slate-900 tracking-tight">
              Email Templates
            </h1>
            <p className="text-xs sm:text-sm text-slate-600 mt-1">
              Select which email template should be followed for outgoing transactional emails sent by your organization.
            </p>
          </div>

          {/* ─── 2. PLATFORM FORMCONTROL DROPDOWN & CURRENT ACTIVE BANNER ─── */}
          <div className="bg-white rounded-2xl border border-slate-200 p-4 sm:p-5 shadow-xs space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div>
                <label className="text-xs font-bold text-slate-900 block mb-0.5">
                  Select Email Type
                </label>
                <p className="text-xs text-slate-500">
                  Choose the email notification category you want to configure.
                </p>
              </div>

              {/* Using Platform FormControl component */}
              <div className="w-full sm:w-72">
                <FormControl
                  type="select"
                  placeholder="Select Email Type"
                  value={activeType}
                  options={STANDARD_EMAIL_TYPES.map((t) => ({
                    id: t.id,
                    name: t.name,
                  }))}
                  onChange={(val) => {
                    setActiveType(val || "contact_reply");
                    setSearchQuery("");
                  }}
                />
              </div>
            </div>

            {/* Currently Active Template Summary Pill */}
            {currentActiveTemplate && (
              <div className="flex items-center gap-2.5 bg-emerald-50/80 border border-emerald-200/80 rounded-xl px-3.5 py-2.5 text-xs text-emerald-900">
                <CheckCircle2 size={16} className="text-emerald-600 shrink-0" />
                <div className="truncate">
                  <span className="font-semibold text-emerald-700">Active template for {currentTypeConfig.name}:</span>{" "}
                  <strong className="font-bold text-emerald-950">{currentActiveTemplate.name}</strong>{" "}
                  <span className="text-emerald-700">
                    ({currentActiveTemplate.source === "organization" ? "Organization Specific" : "Platform Provided"})
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* ─── 3. TEMPLATES SELECTION LIST (DYNAMIC FROM API) ─── */}
          <div className="space-y-3">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 px-1">
              <span className="text-xs font-bold text-slate-700">
                Available Templates ({filteredTemplates.length})
              </span>

              {/* Quick Search */}
              <div className="relative w-full sm:w-64">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search template name..."
                  className="w-full pl-8 pr-7 py-2 rounded-xl border border-slate-200 text-xs bg-white focus:outline-none focus:border-[#115B9D] placeholder:text-slate-400"
                />
                {searchQuery && (
                  <button
                    type="button"
                    onClick={() => setSearchQuery("")}
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 text-xs"
                  >
                    ✕
                  </button>
                )}
              </div>
            </div>

            {loading ? (
              <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center space-y-2">
                <div className="inline-block w-7 h-7 border-3 border-slate-200 border-t-[#115B9D] rounded-full animate-spin" />
                <p className="text-xs font-bold text-slate-600">Loading templates from server...</p>
              </div>
            ) : filteredTemplates.length === 0 ? (
              <div className="bg-white rounded-2xl border border-slate-200 p-10 text-center space-y-2">
                <Mail size={32} className="text-slate-300 mx-auto" />
                <p className="text-xs font-bold text-slate-800">No templates found</p>
                <p className="text-xs text-slate-500">
                  {searchQuery ? "Try clearing your search query." : "No templates currently returned by the API for this category."}
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {filteredTemplates.map((template) => {
                  const id = template.id || template._id;
                  const isSelected = selectedTemplateId === id;
                  const isOrg = template.source === "organization";

                  return (
                    <div
                      key={id}
                      onClick={() => handleSelectTemplate(template)}
                      className={`relative bg-white rounded-2xl border p-4 sm:p-5 transition-all cursor-pointer shadow-xs hover:shadow-md ${
                        isSelected
                          ? "border-emerald-500 ring-2 ring-emerald-500/20 bg-emerald-50/10"
                          : "border-slate-200 hover:border-slate-300"
                      }`}
                    >
                      <div className="flex items-start justify-between gap-3 sm:gap-4">
                        {/* Radio selection circle & Content */}
                        <div className="flex items-start gap-3.5 min-w-0">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSelectTemplate(template);
                            }}
                            className="mt-0.5 text-slate-400 hover:text-[#115B9D] transition-colors shrink-0"
                          >
                            {isSelected ? (
                              <CheckCircle2 size={20} className="text-emerald-600" />
                            ) : (
                              <Circle size={20} className="text-slate-300 hover:text-slate-400" />
                            )}
                          </button>

                          <div className="space-y-1.5 min-w-0">
                            {/* Badges & Name */}
                            <div className="flex flex-wrap items-center gap-2">
                              <h3 className="text-sm sm:text-base font-extrabold text-slate-900 truncate">
                                {template.name}
                              </h3>

                              <span
                                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider border ${
                                  isOrg
                                    ? "bg-emerald-50 text-emerald-800 border-emerald-200"
                                    : "bg-blue-50 text-blue-800 border-blue-200"
                                }`}
                              >
                                {isOrg ? (
                                  <>
                                    <Building2 size={11} />
                                    Organization Specific
                                  </>
                                ) : (
                                  <>
                                    <ShieldCheck size={11} />
                                    Platform Provided
                                  </>
                                )}
                              </span>

                              {isSelected && (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-bold bg-emerald-600 text-white">
                                  Active Template
                                </span>
                              )}
                            </div>

                            {/* Subject Line */}
                            <div className="text-xs font-medium text-slate-700">
                              <span className="text-slate-400 font-bold uppercase text-[10px] mr-1.5">
                                Subject:
                              </span>
                              <span>{template.subject}</span>
                            </div>

                            {/* Description */}
                            {template.description && (
                              <p className="text-xs text-slate-500 line-clamp-1">
                                {template.description}
                              </p>
                            )}
                          </div>
                        </div>

                        {/* Action Buttons: Preview & Select */}
                        <div className="flex items-center gap-2 shrink-0" onClick={(e) => e.stopPropagation()}>
                          <button
                            type="button"
                            onClick={() => setPreviewTemplate(template)}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold text-slate-700 bg-slate-100 hover:bg-slate-200 transition-colors"
                            title="Preview email layout"
                          >
                            <Eye size={13} className="text-slate-500" />
                            <span className="hidden sm:inline">Preview</span>
                          </button>

                          <button
                            type="button"
                            disabled={isSelected || isUpdating}
                            onClick={() => handleSelectTemplate(template)}
                            className={`inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-xl text-xs font-bold transition-all ${
                              isSelected
                                ? "bg-emerald-100 text-emerald-900 border border-emerald-200 cursor-default"
                                : "bg-[#115B9D] text-white hover:bg-[#0e4b82] shadow-xs active:scale-95"
                            }`}
                          >
                            {isSelected ? (
                              <>
                                <Check size={13} className="text-emerald-700" />
                                <span>Active</span>
                              </>
                            ) : (
                              <span>Use This Template</span>
                            )}
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ─── 4. PROPERLY CENTERED LIVE PREVIEW MODAL (ON TOP OF NAVBAR) ─── */}
      {previewTemplate && (
        <div className="fixed inset-0 z-[99999] bg-slate-950/75 backdrop-blur-sm flex items-center justify-center p-3 sm:p-6 overflow-y-auto">
          <div className="relative bg-white rounded-2xl sm:rounded-3xl shadow-2xl border border-slate-200 w-full max-w-3xl my-auto flex flex-col max-h-[85vh] overflow-hidden">
            
            {/* Modal Header */}
            <div className="px-5 py-3.5 sm:py-4 border-b border-slate-200 flex items-center justify-between gap-3 bg-slate-50">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <h3 className="font-extrabold text-sm sm:text-base text-slate-900 truncate">
                    {previewTemplate.name}
                  </h3>
                  <span
                    className={`inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider border ${
                      previewTemplate.source === "organization"
                        ? "bg-emerald-50 text-emerald-800 border-emerald-200"
                        : "bg-blue-50 text-blue-800 border-blue-200"
                    }`}
                  >
                    {previewTemplate.source === "organization" ? "Organization" : "Platform"}
                  </span>
                </div>
                <p className="text-xs text-slate-500 truncate mt-0.5">
                  Subject: {previewTemplate.subject}
                </p>
              </div>

              <div className="flex items-center gap-2 shrink-0">
                {/* Device Viewport Toggle */}
                <div className="inline-flex p-1 rounded-xl bg-slate-200/80 text-xs font-semibold">
                  <button
                    type="button"
                    onClick={() => setPreviewDevice("desktop")}
                    className={`p-1.5 rounded-lg transition-colors ${
                      previewDevice === "desktop" ? "bg-white text-[#115B9D] shadow-xs" : "text-slate-600 hover:text-slate-900"
                    }`}
                    title="Desktop Preview"
                  >
                    <Monitor size={15} />
                  </button>
                  <button
                    type="button"
                    onClick={() => setPreviewDevice("mobile")}
                    className={`p-1.5 rounded-lg transition-colors ${
                      previewDevice === "mobile" ? "bg-white text-[#115B9D] shadow-xs" : "text-slate-600 hover:text-slate-900"
                    }`}
                    title="Mobile Preview"
                  >
                    <Smartphone size={15} />
                  </button>
                </div>

                {/* Close Button */}
                <button
                  type="button"
                  onClick={() => setPreviewTemplate(null)}
                  className="w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-500 hover:text-slate-900 flex items-center justify-center transition-colors cursor-pointer"
                  title="Close Preview"
                >
                  <X size={16} />
                </button>
              </div>
            </div>

            {/* Modal Body Canvas */}
            <div className="flex-1 overflow-y-auto p-4 sm:p-6 bg-slate-100/90 flex justify-center items-start">
              <div
                className={`bg-white rounded-2xl shadow-sm border border-slate-300 overflow-hidden w-full transition-all ${
                  previewDevice === "mobile" ? "max-w-[380px]" : "max-w-[620px]"
                }`}
              >
                <div
                  dangerouslySetInnerHTML={{
                    __html: previewTemplate.body_html || previewTemplate.html || `<div style="padding:24px;text-align:center;color:#64748b;">${previewTemplate.body_text || "No preview content available"}</div>`,
                  }}
                />
              </div>
            </div>

            {/* Modal Footer */}
            <div className="px-5 py-3.5 bg-slate-50 border-t border-slate-200 flex items-center justify-between gap-3">
              <button
                type="button"
                onClick={() => handleCopyHtml(previewTemplate.body_html || previewTemplate.html)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold text-slate-700 bg-white border border-slate-200 hover:bg-slate-100 cursor-pointer"
              >
                {copiedCode ? <Check size={13} className="text-emerald-600" /> : <Copy size={13} className="text-slate-500" />}
                <span>{copiedCode ? "Copied" : "Copy Raw HTML"}</span>
              </button>

              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setPreviewTemplate(null)}
                  className="px-4 py-2 rounded-xl text-xs font-bold text-slate-600 hover:text-slate-900 bg-white border border-slate-200 cursor-pointer"
                >
                  Close
                </button>

                <button
                  type="button"
                  disabled={
                    (previewTemplate.id || previewTemplate._id) === selectedTemplateId || isUpdating
                  }
                  onClick={() => {
                    handleSelectTemplate(previewTemplate);
                    setPreviewTemplate(null);
                  }}
                  className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-bold transition-all cursor-pointer ${
                    (previewTemplate.id || previewTemplate._id) === selectedTemplateId
                      ? "bg-emerald-100 text-emerald-900 border border-emerald-200 cursor-default"
                      : "bg-[#115B9D] text-white hover:bg-[#0e4b82] shadow-xs"
                  }`}
                >
                  {(previewTemplate.id || previewTemplate._id) === selectedTemplateId ? (
                    <>
                      <Check size={13} className="text-emerald-700" />
                      <span>Currently Active</span>
                    </>
                  ) : (
                    <span>Use This Template</span>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}
